<template>
  <v-container fluid class="pa-4">
    <v-alert
      v-if="overdueAgreements.length > 0"
      :value="true"
      color="red darken-2"
      icon="warning"
      class="mb-4"
      outline
    >
      <div class="font-weight-bold mb-2">
        ⚠ Vadesi Geçmiş Anlaşmalar ({{ overdueAgreements.length }})
      </div>
      <v-data-table
        :headers="overdueHeaders"
        :items="overdueAgreements"
        hide-actions
        class="elevation-0 transparent"
      >
        <template v-slot:items="props">
          <tr>
            <td>{{ formatDate(props.item.paymentDate) }}</td>
            <td class="red--text font-weight-bold">{{ props.item.daysOverdue }} gün</td>
            <td>{{ props.item.companyName }}</td>
            <td>{{ props.item.contactPerson }}</td>
            <td class="text-xs-right">{{ formatCurrency(props.item.totalNetCash) }}</td>
            <td class="text-xs-right">{{ props.item.orderCount }}</td>
            <td>
              <v-btn small color="success" @click="openMarkPaidDialog(props.item)">
                <v-icon left small>check_circle</v-icon>
                Ödendi
              </v-btn>
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-alert>

    <v-card class="elevation-2">
      <v-toolbar color="amber darken-3" dark dense>
        <v-icon left>handshake</v-icon>
        <v-toolbar-title>Darphane Anlaşmaları</v-toolbar-title>
        <v-chip
          v-if="computeByCompany"
          small
          color="indigo"
          text-color="white"
          class="ml-3"
        >
          🏢 Şirket bazlı hesaplanıyor
        </v-chip>
      </v-toolbar>

      <v-tabs v-model="activeTab" color="amber lighten-4" slider-color="amber darken-3">
        <v-tab>📅 Para Alış Takvimi</v-tab>
        <v-tab>⏳ Bekleyenler</v-tab>
        <v-tab>📋 Geçmiş</v-tab>

        <v-tab-item>
          <v-card flat>
            <v-card-text>
              <v-layout row wrap class="mb-3">
                <v-flex xs12 sm4 class="px-2">
                  <v-menu
                    v-model="planFromMenu"
                    :close-on-content-click="false"
                    lazy
                    transition="scale-transition"
                    offset-y
                    full-width
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        :value="planFrom"
                        label="Başlangıç Tarihi"
                        prepend-icon="event"
                        readonly
                        v-on="on"
                        dense
                      />
                    </template>
                    <v-date-picker
                      v-model="planFrom"
                      locale="tr-TR"
                      @input="planFromMenu = false; loadPlan()"
                    />
                  </v-menu>
                </v-flex>
                <v-flex xs12 sm4 class="px-2">
                  <v-menu
                    v-model="planToMenu"
                    :close-on-content-click="false"
                    lazy
                    transition="scale-transition"
                    offset-y
                    full-width
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        :value="planTo"
                        label="Bitiş Tarihi"
                        prepend-icon="event"
                        readonly
                        v-on="on"
                        dense
                      />
                    </template>
                    <v-date-picker
                      v-model="planTo"
                      locale="tr-TR"
                      @input="planToMenu = false; loadPlan()"
                    />
                  </v-menu>
                </v-flex>
                <v-flex xs12 sm4 class="px-2 pt-3">
                  <v-btn small color="amber darken-3" class="white--text" @click="loadPlan">
                    <v-icon left small>refresh</v-icon>
                    Yenile
                  </v-btn>
                </v-flex>
              </v-layout>

              <v-layout v-if="plan" row wrap class="mb-3">
                <v-flex xs12 sm6 md3 class="px-2 mb-2">
                  <v-card outlined class="pa-3">
                    <div class="caption grey--text text--darken-1">Toplam Beklenen Net Nakit</div>
                    <div class="title font-weight-bold blue--text">
                      {{ formatCurrency(plan.grandTotalNetCash) }}
                    </div>
                  </v-card>
                </v-flex>
                <v-flex xs12 sm6 md3 class="px-2 mb-2">
                  <v-card outlined class="pa-3">
                    <div class="caption grey--text text--darken-1">Anlaşılmamış Sipariş</div>
                    <div class="title font-weight-bold orange--text">
                      {{ plan.unallocatedOrderCount }} ad.
                    </div>
                  </v-card>
                </v-flex>
                <v-flex xs12 sm6 md3 class="px-2 mb-2">
                  <v-card outlined class="pa-3">
                    <div class="caption grey--text text--darken-1">Devreden Sipariş</div>
                    <div class="title font-weight-bold red--text">
                      🔄 {{ plan.totalCarriedOverOrderCount }} ad.
                    </div>
                  </v-card>
                </v-flex>
                <v-flex xs12 sm6 md3 class="px-2 mb-2">
                  <v-card outlined class="pa-3">
                    <div class="caption grey--text text--darken-1">Toplam Has Gram</div>
                    <div class="title font-weight-bold">
                      {{ formatGram(plan.grandTotalPureGram) }} gr
                    </div>
                  </v-card>
                </v-flex>
              </v-layout>

              <v-data-table
                :headers="planHeaders"
                :items="plan ? plan.days : []"
                :loading="loading"
                hide-actions
                class="elevation-1"
                expand
              >
                <template v-slot:items="props">
                  <tr :class="planRowClass(props.item)">
                    <td class="text-no-wrap">
                      <strong>{{ formatDateWithDay(props.item.paymentDate) }}</strong>
                      <span v-if="isToday(props.item.paymentDate)" class="ml-1 caption red--text">(Bugün)</span>
                    </td>
                    <td class="text-xs-right">
                      {{ props.item.orderCount }}
                      <small class="grey--text">
                        ({{ props.item.allocatedOrderCount }}A / {{ props.item.unallocatedOrderCount }}B)
                      </small>
                      <v-chip
                        v-if="props.item.carriedOverOrderCount > 0"
                        small
                        color="orange"
                        text-color="white"
                        class="ml-1"
                      >
                        🔄 {{ props.item.carriedOverOrderCount }} devreden
                      </v-chip>
                    </td>
                    <td class="text-xs-right">{{ formatCurrency(props.item.totalInvoiceAmount) }}</td>
                    <td class="text-xs-right blue--text font-weight-bold">
                      {{ formatCurrency(props.item.totalNetCash) }}
                    </td>
                    <td class="text-xs-right">{{ formatGram(props.item.totalPureGram) }} gr</td>
                    <td>
                      <template v-if="props.item.existingAgreements && props.item.existingAgreements.length > 0">
                        <v-chip
                          v-for="ag in props.item.existingAgreements"
                          :key="ag.id"
                          small
                          :color="agreementChipColor(ag.status)"
                          text-color="white"
                          style="cursor: pointer"
                          @click="openDetailDialog(ag)"
                        >
                          {{ ag.statusName }} ({{ formatGramPrice(ag.goldGramPrice) }} ₺/gr)
                        </v-chip>
                        <v-chip
                          v-if="props.item.unallocatedOrderCount > 0"
                          small
                          color="orange darken-2"
                          text-color="white"
                          class="font-weight-bold"
                        >
                          <v-icon left small>warning</v-icon>
                          {{ props.item.unallocatedOrderCount }} sipariş bekliyor
                        </v-chip>
                      </template>
                      <span v-else-if="props.item.unallocatedOrderCount > 0">
                        <v-chip small color="orange darken-2" text-color="white" class="font-weight-bold">
                          <v-icon left small>warning</v-icon>
                          Anlaşma yok · {{ props.item.unallocatedOrderCount }} sipariş
                        </v-chip>
                      </span>
                      <span v-else class="grey--text caption">—</span>
                    </td>
                    <td>
                      <v-btn small color="primary" outline @click="openDayOrdersDialog(props.item)">
                        <v-icon left small>visibility</v-icon>
                        Detay
                      </v-btn>
                      <v-btn
                        small
                        color="success"
                        :disabled="props.item.unallocatedOrderCount === 0"
                        @click="openCreateDialog(props.item)"
                      >
                        <v-icon left small>add_circle</v-icon>
                        Anlaşma Yap
                      </v-btn>
                    </td>
                  </tr>
                  <tr
                    v-if="props.item.unallocatedHasUnmatchedCity"
                    class="orange lighten-5"
                  >
                    <td colspan="7" class="text-no-wrap">
                      <v-icon small color="orange darken-2">warning</v-icon>
                      <span class="orange--text text--darken-3 caption ml-1">
                        Bu güne dahil bazı siparişlerin şehri tanımsız (duration=0 varsayıldı). DeliveryCities tablosunu güncelleyin.
                      </span>
                    </td>
                  </tr>
                </template>
                <template v-slot:no-data>
                  <v-alert :value="true" color="info" icon="info">
                    Seçilen tarih aralığında plan bulunmamaktadır.
                  </v-alert>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-tab-item>

        <v-tab-item>
          <v-card flat>
            <v-card-text>
              <v-data-table
                :headers="agreementHeaders"
                :items="pendingAgreements"
                :loading="loading"
                hide-actions
                class="elevation-1"
              >
                <template v-slot:items="props">
                  <tr>
                    <td class="text-no-wrap">{{ formatDate(props.item.agreementDate) }}</td>
                    <td>{{ props.item.companyName }}</td>
                    <td>{{ props.item.contactPerson }}</td>
                    <td>{{ props.item.createdByName || '—' }}</td>
                    <td class="text-no-wrap">{{ formatDate(props.item.paymentDate) }}</td>
                    <td class="text-xs-right">{{ props.item.orderCount }}</td>
                    <td class="text-xs-right">{{ formatCurrency(props.item.totalInvoiceAmount) }}</td>
                    <td class="text-xs-right">{{ formatCurrency(props.item.totalNetCash) }}</td>
                    <td class="text-xs-right blue--text font-weight-bold">
                      {{ formatCurrency(props.item.agreedAmount) }}
                      <span v-if="agreementExtra(props.item) > 0" class="caption green--text d-block">
                        +{{ formatCurrency(agreementExtra(props.item)) }}
                      </span>
                      <span v-else-if="agreementExtra(props.item) < 0" class="caption red--text d-block">
                        {{ formatCurrency(agreementExtra(props.item)) }}
                      </span>
                    </td>
                    <td class="text-xs-right">{{ formatGramPrice(props.item.goldGramPrice) }}</td>
                    <td class="text-xs-right">{{ formatGram(props.item.totalPureGram) }}</td>
                    <td class="text-xs-right blue--text font-weight-bold">
                      {{ formatGram(agreementAcquiredGram(props.item)) }}
                      <span v-if="agreementAcquiredDelta(props.item) > 0" class="caption green--text d-block">
                        +{{ formatGram(agreementAcquiredDelta(props.item)) }}
                      </span>
                      <span v-else-if="agreementAcquiredDelta(props.item) < 0" class="caption red--text d-block">
                        {{ formatGram(agreementAcquiredDelta(props.item)) }}
                      </span>
                    </td>
                    <td class="text-no-wrap text-xs-center">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                          <v-btn icon small color="primary" v-on="on" @click="openDetailDialog(props.item)">
                            <v-icon small>visibility</v-icon>
                          </v-btn>
                        </template>
                        <span>Detay</span>
                      </v-tooltip>
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                          <v-btn icon small color="amber darken-3" v-on="on" @click="openEditDialog(props.item)">
                            <v-icon small>edit</v-icon>
                          </v-btn>
                        </template>
                        <span>Düzenle</span>
                      </v-tooltip>
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                          <v-btn icon small color="success" v-on="on" @click="openMarkPaidDialog(props.item)">
                            <v-icon small>check_circle</v-icon>
                          </v-btn>
                        </template>
                        <span>Ödendi</span>
                      </v-tooltip>
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                          <v-btn icon small color="error" v-on="on" @click="openCancelDialog(props.item)">
                            <v-icon small>cancel</v-icon>
                          </v-btn>
                        </template>
                        <span>İptal</span>
                      </v-tooltip>
                    </td>
                  </tr>
                </template>
                <template v-slot:no-data>
                  <v-alert :value="true" color="info" icon="info">
                    Bekleyen anlaşma yok.
                  </v-alert>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-tab-item>

        <v-tab-item>
          <v-card flat>
            <v-card-text>
              <v-layout row wrap class="mb-3">
                <v-flex xs12 sm3 class="px-2">
                  <v-menu
                    v-model="historyFromMenu"
                    :close-on-content-click="false"
                    lazy
                    transition="scale-transition"
                    offset-y
                    full-width
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        :value="historyFrom"
                        label="Başlangıç"
                        prepend-icon="event"
                        readonly
                        v-on="on"
                        dense
                        clearable
                        @click:clear="historyFrom = null; loadHistory()"
                      />
                    </template>
                    <v-date-picker
                      v-model="historyFrom"
                      locale="tr-TR"
                      @input="historyFromMenu = false; loadHistory()"
                    />
                  </v-menu>
                </v-flex>
                <v-flex xs12 sm3 class="px-2">
                  <v-menu
                    v-model="historyToMenu"
                    :close-on-content-click="false"
                    lazy
                    transition="scale-transition"
                    offset-y
                    full-width
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        :value="historyTo"
                        label="Bitiş"
                        prepend-icon="event"
                        readonly
                        v-on="on"
                        dense
                        clearable
                        @click:clear="historyTo = null; loadHistory()"
                      />
                    </template>
                    <v-date-picker
                      v-model="historyTo"
                      locale="tr-TR"
                      @input="historyToMenu = false; loadHistory()"
                    />
                  </v-menu>
                </v-flex>
                <v-flex xs12 sm3 class="px-2">
                  <v-select
                    v-model="historyStatus"
                    :items="historyStatusOptions"
                    label="Durum"
                    dense
                    clearable
                    @change="loadHistory"
                  />
                </v-flex>
                <v-flex xs12 sm3 class="px-2 pt-3">
                  <v-btn small color="amber darken-3" class="white--text" @click="loadHistory">
                    <v-icon left small>refresh</v-icon>
                    Yenile
                  </v-btn>
                </v-flex>
              </v-layout>

              <v-data-table
                :headers="historyHeaders"
                :items="historyAgreements"
                :loading="loading"
                hide-actions
                class="elevation-1"
              >
                <template v-slot:items="props">
                  <tr>
                    <td class="text-no-wrap">{{ formatDate(props.item.agreementDate) }}</td>
                    <td>
                      <v-chip small :color="agreementChipColor(props.item.status)" text-color="white">
                        {{ props.item.statusName }}
                      </v-chip>
                    </td>
                    <td>{{ props.item.companyName }}</td>
                    <td>{{ props.item.contactPerson }}</td>
                    <td>{{ props.item.createdByName || '—' }}</td>
                    <td class="text-no-wrap">{{ formatDate(props.item.paymentDate) }}</td>
                    <td class="text-no-wrap">{{ props.item.paidAt ? formatDate(props.item.paidAt) : '—' }}</td>
                    <td class="text-xs-right">{{ props.item.orderCount }}</td>
                    <td class="text-xs-right">{{ formatCurrency(props.item.totalNetCash) }}</td>
                    <td class="text-xs-right blue--text font-weight-bold">
                      {{ formatCurrency(props.item.agreedAmount) }}
                      <span v-if="agreementExtra(props.item) > 0" class="caption green--text d-block">
                        +{{ formatCurrency(agreementExtra(props.item)) }}
                      </span>
                      <span v-else-if="agreementExtra(props.item) < 0" class="caption red--text d-block">
                        {{ formatCurrency(agreementExtra(props.item)) }}
                      </span>
                    </td>
                    <td class="text-xs-right">{{ formatGramPrice(props.item.goldGramPrice) }}</td>
                    <td class="text-xs-right blue--text font-weight-bold">
                      {{ formatGram(agreementAcquiredGram(props.item)) }}
                    </td>
                    <td class="text-no-wrap text-xs-center">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                          <v-btn icon small color="primary" v-on="on" @click="openDetailDialog(props.item)">
                            <v-icon small>visibility</v-icon>
                          </v-btn>
                        </template>
                        <span>Detay</span>
                      </v-tooltip>
                    </td>
                  </tr>
                </template>
                <template v-slot:no-data>
                  <v-alert :value="true" color="info" icon="info">
                    Geçmiş kayıt yok.
                  </v-alert>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-tab-item>
      </v-tabs>
    </v-card>

    <v-dialog v-model="createDialog" persistent max-width="640">
      <v-card v-if="createTarget">
        <v-card-title class="amber darken-3 white--text">
          <v-icon left color="white">{{ editingId ? 'edit' : 'handshake' }}</v-icon>
          <span v-if="editingId">
            Anlaşmayı Düzenle — {{ formatDateWithDay(createTarget.paymentDate) }}
          </span>
          <span v-else>
            {{ formatDateWithDay(createTarget.paymentDate) }}
            <span class="ml-2">— {{ createTarget.unallocatedOrderCount }} sipariş için Darphane Anlaşması</span>
          </span>
        </v-card-title>
        <v-card-text class="pa-4">
          <v-card outlined class="pa-3 mb-3 grey lighten-4">
            <div class="body-2">
              <strong>Sipariş sayısı:</strong> {{ createTarget.unallocatedOrderCount }}
              <span v-if="createTarget.carriedOverOrderCount > 0" class="orange--text text--darken-2">
                ({{ createTarget.unallocatedOrderCount - createTarget.carriedOverOrderCount }} bugünün +
                <strong>{{ createTarget.carriedOverOrderCount }} devreden</strong>)
              </span>
            </div>
            <div
              v-if="createTarget.carriedOverOrderCount > 0"
              class="caption orange--text text--darken-2 mt-1"
            >
              ⓘ {{ createTarget.carriedOverOrderCount }} sipariş geçmişten devretti
              ({{ formatCurrency(createTarget.carriedOverNetCash) }} net cash) — bu anlaşmaya dahil edilecek.
            </div>
            <v-divider class="my-2" />
            <div class="body-2">
              <strong>Fatura toplamı:</strong> {{ formatCurrency(createTarget.unallocatedInvoiceAmount) }}
            </div>
            <div class="title blue--text font-weight-bold">
              Net hesaba yatacak: {{ formatCurrency(createTarget.unallocatedNetCash) }}
            </div>
            <div class="body-2 mt-1">
              <strong>Toplam has gram:</strong>
              {{ formatGram(unallocatedPureGram(createTarget)) }} gr
            </div>
          </v-card>

          <v-text-field
            v-model="createForm.companyName"
            label="Firma Adı"
            prepend-icon="business"
            :rules="[v => !!v && v.trim().length > 0 || 'Firma adı zorunludur']"
            maxlength="100"
            counter="100"
            required
            dense
          />
          <v-text-field
            v-model="createForm.contactPerson"
            label="Firmada Anlaşılan Yetkili"
            prepend-icon="person"
            :rules="[v => !!v && v.trim().length > 0 || 'Yetkili zorunludur']"
            maxlength="100"
            counter="100"
            required
            dense
          />
          <v-text-field
            v-model.number="createForm.goldGramPrice"
            label="Gram Has Altın Fiyatı (₺/gr)"
            prepend-icon="payments"
            type="number"
            min="0"
            step="0.01"
            :rules="[v => v > 0 || 'Gram fiyatı sıfırdan büyük olmalı']"
            required
            dense
          />
          <v-text-field
            v-model.number="createForm.agreedAmount"
            label="Anlaşılan Tutar (₺) — boş bırakırsanız sistem hesabı kullanılır"
            prepend-icon="account_balance_wallet"
            type="number"
            min="0"
            step="0.01"
            dense
          />
          <div class="caption grey--text text--darken-1 ml-4">
            Sistem hesabı: <strong>{{ formatCurrency(createTarget.unallocatedNetCash) }}</strong>
            <span v-if="agreedExtra !== null && agreedExtra !== 0" :class="agreedExtra > 0 ? 'green--text font-weight-bold' : 'red--text font-weight-bold'">
              · {{ agreedExtra > 0 ? 'Ekstra' : 'Eksik' }}:
              {{ agreedExtra > 0 ? '+' : '' }}{{ formatCurrency(agreedExtra) }}
            </span>
          </div>
          <div v-if="agreementGoldAcquired !== null" class="caption grey--text text--darken-1 ml-4 mt-1">
            Bu fiyatla anlaşırsanız: {{ formatCurrency(effectiveAgreedAmount) }} ÷ {{ formatGramPrice(createForm.goldGramPrice) }} ₺/gr =
            <strong>{{ formatGram(agreementGoldAcquired) }} gr</strong> has altın alırsınız.
            <span v-if="agreementGoldDelta !== null" :class="agreementGoldDelta >= 0 ? 'green--text' : 'red--text'">
              · Siparişlerin has gramı {{ formatGram(unallocatedPureGram(createTarget)) }} gr →
              {{ agreementGoldDelta >= 0 ? 'Kar' : 'Zarar' }}:
              <strong>{{ agreementGoldDelta >= 0 ? '+' : '' }}{{ formatGram(agreementGoldDelta) }} gr</strong>
            </span>
          </div>
          <v-textarea
            v-model="createForm.notes"
            label="Notlar (opsiyonel)"
            prepend-icon="notes"
            rows="2"
            auto-grow
            dense
          />
          <div class="caption grey--text text--darken-1 mt-2">
            Anlaşmayı kaydeden: <strong>{{ currentUserName }}</strong>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn flat @click="createDialog = false" :disabled="creating">İptal</v-btn>
          <v-btn
            color="success"
            :loading="creating"
            :disabled="!createFormValid"
            @click="submitCreate"
          >
            {{ editingId ? 'Güncelle' : 'Anlaşmayı Kaydet' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="markPaidDialog" persistent max-width="420">
      <v-card v-if="markPaidTarget">
        <v-card-title class="success white--text">
          <v-icon left color="white">check_circle</v-icon>
          Ödeme Onayı
        </v-card-title>
        <v-card-text class="pa-4">
          <div class="body-2">
            <strong>{{ markPaidTarget.companyName }}</strong> firmasına
            <strong>{{ formatCurrency(markPaidTarget.totalNetCash) }}</strong> ödemenizi
            onaylıyor musunuz?
          </div>
          <v-menu
            v-model="paidAtMenu"
            :close-on-content-click="false"
            lazy
            transition="scale-transition"
            offset-y
            full-width
            min-width="290px"
            class="mt-3"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                :value="markPaidForm.paidAt"
                label="Ödeme Tarihi (opsiyonel — boş bırakırsanız bugün)"
                prepend-icon="event"
                readonly
                v-on="on"
                dense
                clearable
                @click:clear="markPaidForm.paidAt = null"
              />
            </template>
            <v-date-picker
              v-model="markPaidForm.paidAt"
              locale="tr-TR"
              @input="paidAtMenu = false"
            />
          </v-menu>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn flat @click="markPaidDialog = false" :disabled="processing">Vazgeç</v-btn>
          <v-btn color="success" :loading="processing" @click="submitMarkPaid">
            Ödendi
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="cancelDialog" persistent max-width="420">
      <v-card v-if="cancelTarget">
        <v-card-title class="error white--text">
          <v-icon left color="white">cancel</v-icon>
          Anlaşmayı İptal Et
        </v-card-title>
        <v-card-text class="pa-4">
          <div class="body-2">
            <strong>{{ cancelTarget.companyName }}</strong> ile yapılan
            <strong>{{ formatCurrency(cancelTarget.totalNetCash) }}</strong> tutarındaki anlaşmayı iptal etmek üzeresiniz.
            <br />
            Anlaşmaya bağlı <strong>{{ cancelTarget.orderCount }}</strong> sipariş tekrar anlaşılmamış havuzuna dönecek.
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn flat @click="cancelDialog = false" :disabled="processing">Vazgeç</v-btn>
          <v-btn color="error" :loading="processing" @click="submitCancel">
            İptal Et
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <mint-agreement-detail-dialog
      v-model="detailDialog"
      :agreement-id="detailAgreementId"
      @error="showError"
    />

    <v-dialog v-model="dayOrdersDialog" persistent max-width="1100">
      <v-card v-if="dayOrdersData">
        <v-card-title class="primary white--text">
          <v-icon left color="white">visibility</v-icon>
          {{ formatDateWithDay(dayOrdersData.paymentDate) }} — Siparişler
          <v-spacer />
          <v-btn icon dark @click="dayOrdersDialog = false"><v-icon>close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="pa-4">
          <v-layout row wrap class="mb-2">
            <v-flex xs12 sm3 class="px-2">
              <div class="caption grey--text text--darken-1">Toplam Sipariş</div>
              <div class="subtitle-1 font-weight-bold">{{ dayOrdersData.orderCount }}</div>
            </v-flex>
            <v-flex xs12 sm3 class="px-2">
              <div class="caption grey--text text--darken-1">Anlaşılmış / Bekleyen</div>
              <div class="subtitle-1">
                <span class="green--text font-weight-bold">{{ dayOrdersData.allocatedOrderCount }}</span>
                /
                <span class="orange--text font-weight-bold">{{ dayOrdersData.unallocatedOrderCount }}</span>
              </div>
            </v-flex>
            <v-flex xs12 sm3 class="px-2">
              <div class="caption grey--text text--darken-1">Fatura Toplamı</div>
              <div class="subtitle-1">{{ formatCurrency(dayOrdersData.totalInvoiceAmount) }}</div>
            </v-flex>
            <v-flex xs12 sm3 class="px-2">
              <div class="caption grey--text text--darken-1">Net Hesaba Yatacak</div>
              <div class="subtitle-1 blue--text font-weight-bold">
                {{ formatCurrency(dayOrdersData.totalNetCash) }}
              </div>
            </v-flex>
          </v-layout>

          <v-data-table
            :headers="dayOrderHeaders"
            :items="dayOrdersData.orders || []"
            hide-actions
            class="elevation-1"
          >
            <template v-slot:items="props">
              <tr>
                <td>{{ props.item.orderNumber }}</td>
                <td class="text-no-wrap">{{ formatDate(props.item.completedDate) }}</td>
                <td>{{ props.item.customerName }}</td>
                <td>{{ props.item.shippingCity }}</td>
                <td class="text-xs-right">{{ formatCurrency(props.item.invoiceAmount) }}</td>
                <td class="text-xs-right">{{ formatCurrency(props.item.netCash) }}</td>
                <td class="text-xs-right">{{ formatGram(props.item.totalPureGram) }} gr</td>
                <td>
                  <v-chip
                    v-if="props.item.mintAgreementId"
                    small
                    color="green"
                    text-color="white"
                    style="cursor: pointer"
                    @click="openDetailDialog({ id: props.item.mintAgreementId })"
                  >
                    Anlaşılı · Anlaşma #{{ props.item.mintAgreementId }}
                  </v-chip>
                  <v-chip
                    v-else-if="props.item.isCarriedOver"
                    small
                    color="orange"
                    text-color="white"
                  >
                    🔄 Devreden
                  </v-chip>
                  <v-chip v-else small color="grey lighten-1" text-color="white">
                    Bekleyen
                  </v-chip>
                </td>
              </tr>
            </template>
            <template v-slot:no-data>
              <v-alert :value="true" color="info" icon="info">
                Bu güne ait sipariş yok.
              </v-alert>
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn flat @click="dayOrdersDialog = false">Kapat</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="snackbarTimeout" top>
      {{ snackbarMessage }}
      <v-btn flat @click="snackbar = false">Kapat</v-btn>
    </v-snackbar>
  </v-container>
</template>

<script>
import { authenticationService } from '@/_services';
import MintAgreementDetailDialog from '@/components/MintAgreementDetailDialog.vue';

export default {
  name: 'AdminMintAgreements',
  components: { MintAgreementDetailDialog },
  data() {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const plus14 = new Date(today);
    plus14.setDate(plus14.getDate() + 14);
    return {
      activeTab: 0,
      loading: false,

      planFrom: this.toIsoDate(tomorrow),
      planTo: this.toIsoDate(plus14),
      planFromMenu: false,
      planToMenu: false,

      historyFrom: null,
      historyTo: null,
      historyFromMenu: false,
      historyToMenu: false,
      historyStatus: 1,
      historyStatusOptions: [
        { text: 'Ödendi', value: 1 },
        { text: 'İptal Edildi', value: 2 },
      ],

      createDialog: false,
      createTarget: null,
      creating: false,
      editingId: null,
      createForm: {
        companyName: '',
        contactPerson: '',
        goldGramPrice: 0,
        agreedAmount: null,
        notes: '',
      },

      markPaidDialog: false,
      markPaidTarget: null,
      markPaidForm: { paidAt: null },
      paidAtMenu: false,

      cancelDialog: false,
      cancelTarget: null,
      processing: false,

      detailDialog: false,
      detailAgreementId: null,

      dayOrdersDialog: false,
      dayOrdersData: null,
      dayOrderHeaders: [
        { text: 'Sipariş No', value: 'orderNumber', sortable: false },
        { text: 'Tamamlanma', value: 'completedDate', sortable: false },
        { text: 'Müşteri', value: 'customerName', sortable: false },
        { text: 'Şehir', value: 'shippingCity', sortable: false },
        { text: 'Fatura', value: 'invoiceAmount', sortable: false, align: 'right' },
        { text: 'Net Cash', value: 'netCash', sortable: false, align: 'right' },
        { text: 'Has Gram', value: 'totalPureGram', sortable: false, align: 'right' },
        { text: 'Durum', value: 'status', sortable: false },
      ],

      snackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',
      snackbarTimeout: 4000,

      planHeaders: [
        { text: 'Ödeme Günü', value: 'paymentDate', sortable: false },
        { text: 'Sipariş #', value: 'orderCount', sortable: false, align: 'right' },
        { text: 'Fatura Toplamı', value: 'totalInvoiceAmount', sortable: false, align: 'right' },
        { text: 'Net Hesaba Yatacak (₺)', value: 'totalNetCash', sortable: false, align: 'right' },
        { text: 'Has Gram', value: 'totalPureGram', sortable: false, align: 'right' },
        { text: 'Anlaşma Durumu', value: 'agreementStatus', sortable: false },
        { text: 'İşlem', value: 'action', sortable: false },
      ],
      agreementHeaders: [
        { text: 'Tarih', value: 'agreementDate', sortable: false },
        { text: 'Firma', value: 'companyName', sortable: false },
        { text: 'Yetkili', value: 'contactPerson', sortable: false },
        { text: 'Kaydeden', value: 'createdByName', sortable: false },
        { text: 'Vade', value: 'paymentDate', sortable: false },
        { text: 'Sip. #', value: 'orderCount', sortable: false, align: 'right' },
        { text: 'Fatura', value: 'totalInvoiceAmount', sortable: false, align: 'right' },
        { text: 'Net Cash', value: 'totalNetCash', sortable: false, align: 'right' },
        { text: 'Anlaşılan', value: 'agreedAmount', sortable: false, align: 'right' },
        { text: '₺/gr', value: 'goldGramPrice', sortable: false, align: 'right' },
        { text: 'Sip. Gr', value: 'totalPureGram', sortable: false, align: 'right' },
        { text: 'Alınacak', value: 'acquiredGram', sortable: false, align: 'right' },
        { text: 'İşlem', value: 'action', sortable: false, align: 'center' },
      ],
      historyHeaders: [
        { text: 'Tarih', value: 'agreementDate', sortable: false },
        { text: 'Durum', value: 'statusName', sortable: false },
        { text: 'Firma', value: 'companyName', sortable: false },
        { text: 'Yetkili', value: 'contactPerson', sortable: false },
        { text: 'Kaydeden', value: 'createdByName', sortable: false },
        { text: 'Vade', value: 'paymentDate', sortable: false },
        { text: 'Ödendi', value: 'paidAt', sortable: false },
        { text: 'Sip. #', value: 'orderCount', sortable: false, align: 'right' },
        { text: 'Net Cash', value: 'totalNetCash', sortable: false, align: 'right' },
        { text: 'Anlaşılan', value: 'agreedAmount', sortable: false, align: 'right' },
        { text: '₺/gr', value: 'goldGramPrice', sortable: false, align: 'right' },
        { text: 'Alınacak', value: 'acquiredGram', sortable: false, align: 'right' },
        { text: 'İşlem', value: 'action', sortable: false },
      ],
      overdueHeaders: [
        { text: 'Vade', value: 'paymentDate', sortable: false },
        { text: 'Geciken', value: 'daysOverdue', sortable: false },
        { text: 'Firma', value: 'companyName', sortable: false },
        { text: 'Yetkili', value: 'contactPerson', sortable: false },
        { text: 'Net Cash', value: 'totalNetCash', sortable: false, align: 'right' },
        { text: 'Sipariş #', value: 'orderCount', sortable: false, align: 'right' },
        { text: '', value: 'action', sortable: false },
      ],
    };
  },
  computed: {
    plan() {
      return this.$store.getters['mintAgreements/plan'];
    },
    pendingAgreements() {
      return this.$store.getters['mintAgreements/pendingAgreements'];
    },
    historyAgreements() {
      return this.$store.getters['mintAgreements/paidOrCancelledAgreements'];
    },
    overdueAgreements() {
      return (this.plan && this.plan.overdueAgreements) || [];
    },
    computeByCompany() {
      return !!(this.plan && this.plan.computeByCompany);
    },
    currentUserName() {
      const u = authenticationService.currentUserValue;
      if (!u) return '—';
      return u.firstName || u.username || u.userName || '—';
    },
    createFormValid() {
      return (
        !!this.createForm.companyName && this.createForm.companyName.trim().length > 0 &&
        !!this.createForm.contactPerson && this.createForm.contactPerson.trim().length > 0 &&
        Number(this.createForm.goldGramPrice) > 0
      );
    },
    effectiveAgreedAmount() {
      if (!this.createTarget) return 0;
      const manual = Number(this.createForm.agreedAmount);
      if (this.createForm.agreedAmount !== null && this.createForm.agreedAmount !== '' && manual > 0) {
        return manual;
      }
      return Number(this.createTarget.unallocatedNetCash) || 0;
    },
    agreedExtra() {
      if (!this.createTarget) return null;
      const manual = Number(this.createForm.agreedAmount);
      if (this.createForm.agreedAmount === null || this.createForm.agreedAmount === '' || manual <= 0) {
        return null;
      }
      const systemCash = Number(this.createTarget.unallocatedNetCash) || 0;
      return manual - systemCash;
    },
    agreementGoldAcquired() {
      const cash = this.effectiveAgreedAmount;
      const price = Number(this.createForm.goldGramPrice);
      if (!cash || !price || price <= 0) return null;
      return cash / price;
    },
    agreementGoldDelta() {
      if (this.agreementGoldAcquired === null) return null;
      const orderGram = this.unallocatedPureGram(this.createTarget);
      if (!orderGram) return null;
      return this.agreementGoldAcquired - orderGram;
    },
  },
  watch: {
    activeTab(newVal) {
      if (newVal === 0) this.loadPlan();
      else if (newVal === 1) this.loadPending();
      else if (newVal === 2) this.loadHistory();
    },
    '$route.query.tab'() {
      this.applyTabFromRoute();
    },
  },
  mounted() {
    this.applyTabFromRoute();
    if (this.activeTab === 0) this.loadPlan();
  },
  methods: {
    applyTabFromRoute() {
      const tab = this.$route.query.tab;
      if (tab === 'pending') this.activeTab = 1;
      else if (tab === 'history') this.activeTab = 2;
      else if (tab === 'plan') this.activeTab = 0;
    },
    async loadPlan() {
      try {
        await this.$store.dispatch('mintAgreements/getUpcomingPlan', {
          from: this.planFrom,
          to: this.planTo,
        });
      } catch (e) {
        this.showError(e);
      }
    },
    async loadPending() {
      try {
        await this.$store.dispatch('mintAgreements/getList', { status: 0 });
      } catch (e) {
        this.showError(e);
      }
    },
    async loadHistory() {
      try {
        const filters = {};
        if (this.historyFrom) filters.from = this.historyFrom;
        if (this.historyTo) filters.to = this.historyTo;
        if (this.historyStatus !== null && this.historyStatus !== undefined) {
          filters.status = this.historyStatus;
        }
        await this.$store.dispatch('mintAgreements/getList', filters);
      } catch (e) {
        this.showError(e);
      }
    },

    openCreateDialog(day) {
      this.editingId = null;
      this.createTarget = day;
      this.createForm = {
        companyName: '',
        contactPerson: '',
        goldGramPrice: 0,
        agreedAmount: null,
        notes: '',
      };
      this.createDialog = true;
    },
    openEditDialog(agreement) {
      this.editingId = agreement.id;
      this.createTarget = {
        paymentDate: agreement.paymentDate,
        unallocatedOrderCount: agreement.orderCount,
        unallocatedNetCash: agreement.totalNetCash,
        unallocatedInvoiceAmount: agreement.totalInvoiceAmount,
        totalPureGram: agreement.totalPureGram,
        carriedOverOrderCount: 0,
        carriedOverNetCash: 0,
        existingAgreements: [],
      };
      this.createForm = {
        companyName: agreement.companyName,
        contactPerson: agreement.contactPerson,
        goldGramPrice: agreement.goldGramPrice,
        agreedAmount: Number(agreement.agreedAmount) === Number(agreement.totalNetCash) ? null : agreement.agreedAmount,
        notes: agreement.notes || '',
      };
      this.createDialog = true;
    },
    async submitCreate() {
      if (!this.createFormValid || !this.createTarget) return;
      this.creating = true;
      try {
        const payload = {
          goldGramPrice: Number(this.createForm.goldGramPrice),
          companyName: this.createForm.companyName.trim(),
          contactPerson: this.createForm.contactPerson.trim(),
          notes: this.createForm.notes ? this.createForm.notes.trim() : null,
        };
        if (this.createForm.agreedAmount !== null && this.createForm.agreedAmount !== '' && Number(this.createForm.agreedAmount) > 0) {
          payload.agreedAmount = Number(this.createForm.agreedAmount);
        }
        if (this.editingId) {
          await this.$store.dispatch('mintAgreements/updateAgreement', { id: this.editingId, payload });
          this.showSuccess('Anlaşma güncellendi');
        } else {
          payload.paymentDate = this.createTarget.paymentDate;
          await this.$store.dispatch('mintAgreements/createAgreement', payload);
          this.showSuccess(`Anlaşma kaydedildi (${this.createTarget.unallocatedOrderCount} sipariş bağlandı)`);
        }
        this.createDialog = false;
        await this.loadPlan();
        if (this.activeTab === 1) await this.loadPending();
      } catch (e) {
        this.showError(e);
      } finally {
        this.creating = false;
      }
    },

    openMarkPaidDialog(agreement) {
      this.markPaidTarget = agreement;
      this.markPaidForm = { paidAt: null };
      this.markPaidDialog = true;
    },
    async submitMarkPaid() {
      if (!this.markPaidTarget) return;
      this.processing = true;
      try {
        await this.$store.dispatch('mintAgreements/markPaid', {
          id: this.markPaidTarget.id,
          paidAt: this.markPaidForm.paidAt,
        });
        this.markPaidDialog = false;
        this.showSuccess('Anlaşma ödendi olarak işaretlendi');
        await this.loadPlan();
        if (this.activeTab === 1) await this.loadPending();
      } catch (e) {
        this.showError(e);
      } finally {
        this.processing = false;
      }
    },

    openCancelDialog(agreement) {
      this.cancelTarget = agreement;
      this.cancelDialog = true;
    },
    async submitCancel() {
      if (!this.cancelTarget) return;
      this.processing = true;
      try {
        await this.$store.dispatch('mintAgreements/cancelAgreement', this.cancelTarget.id);
        this.cancelDialog = false;
        this.showSuccess('Anlaşma iptal edildi, siparişler havuza döndü');
        await this.loadPlan();
        if (this.activeTab === 1) await this.loadPending();
      } catch (e) {
        this.showError(e);
      } finally {
        this.processing = false;
      }
    },

    openDetailDialog(agreement) {
      this.detailAgreementId = agreement.id;
      this.detailDialog = true;
    },

    openDayOrdersDialog(day) {
      this.dayOrdersData = day;
      this.dayOrdersDialog = true;
    },

    toIsoDate(d) {
      const dt = new Date(d);
      const y = dt.getFullYear();
      const m = String(dt.getMonth() + 1).padStart(2, '0');
      const day = String(dt.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    },
    formatDate(v) {
      if (!v) return '—';
      const d = new Date(v);
      return d.toLocaleDateString('tr-TR');
    },
    formatDateWithDay(v) {
      if (!v) return '—';
      const d = new Date(v);
      const dayName = d.toLocaleDateString('tr-TR', { weekday: 'short' });
      return `${d.toLocaleDateString('tr-TR')} ${dayName}`;
    },
    formatDateTime(v) {
      if (!v) return '—';
      const d = new Date(v);
      return `${d.toLocaleDateString('tr-TR')} ${d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}`;
    },
    formatCurrency(v) {
      const n = Number(v) || 0;
      return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 2 }).format(n);
    },
    formatGram(v) {
      const n = Number(v) || 0;
      return new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 4, maximumFractionDigits: 4 }).format(n);
    },
    formatGramPrice(v) {
      const n = Number(v) || 0;
      return new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
    },
    isToday(v) {
      if (!v) return false;
      const d = new Date(v);
      const t = new Date();
      return d.toDateString() === t.toDateString();
    },
    planRowClass(day) {
      if (!day) return '';
      const today = this.isToday(day.paymentDate);
      const hasPending = (day.unallocatedOrderCount || 0) > 0;
      if (hasPending) return today ? 'orange lighten-4' : 'orange lighten-5';
      if (today) return 'amber lighten-5';
      return '';
    },
    unallocatedPureGram(day) {
      const allocPure = (day.existingAgreements || []).reduce((s, a) => s + (a.totalPureGram || 0), 0);
      return Math.max(0, (day.totalPureGram || 0) - allocPure);
    },
    agreementChipColor(status) {
      if (status === 0) return 'orange';
      if (status === 1) return 'green';
      return 'grey';
    },
    agreementExtra(agreement) {
      if (!agreement) return 0;
      const agreed = Number(agreement.agreedAmount) || 0;
      const net = Number(agreement.totalNetCash) || 0;
      return agreed - net;
    },
    agreementAcquiredGram(agreement) {
      if (!agreement) return 0;
      const agreed = Number(agreement.agreedAmount) || 0;
      const price = Number(agreement.goldGramPrice) || 0;
      if (!agreed || !price) return 0;
      return agreed / price;
    },
    agreementAcquiredDelta(agreement) {
      const acquired = this.agreementAcquiredGram(agreement);
      const sipGram = Number(agreement && agreement.totalPureGram) || 0;
      if (!acquired || !sipGram) return 0;
      return acquired - sipGram;
    },
    showSuccess(msg) {
      this.snackbarMessage = msg;
      this.snackbarColor = 'success';
      this.snackbarTimeout = 4000;
      this.snackbar = true;
    },
    showError(e) {
      const msg = (e && e.response && e.response.data && e.response.data.message) || (e && e.message) || 'Bir hata oluştu';
      this.snackbarMessage = msg;
      this.snackbarColor = 'error';
      this.snackbarTimeout = 8000;
      this.snackbar = true;
    },
  },
};
</script>

<style scoped>
.transparent {
  background: transparent !important;
}
</style>
